import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Popconfirm,
  Typography,
  message,
  Tag,
  Tabs,
  Modal,
  Descriptions,
  Divider,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import InventoryUpdateServ from "../../service/InventoryUpdateServ";
import ProductServ from "../../service/ProductServ";
import AddInventoryUpdate from "../../component/AddInventoryUpdate";
import ShopServ from "../../service/ShopServ";
import "../../css/inventoryUpdate.css";
import "../../css/shops.css";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import TableImage from "../../component/ui/TableImage";
import { getImageUrl as getNormalizedImageUrl } from "../../component/SafeImage";
import { usePreference } from "../../context/PreferenceContext";

const { Title } = Typography;

const InventoryUpdate = () => {
  const { formatPrice } = usePreference();
  const [activeTab, setActiveTab] = useState("in-stock");
  const [searchText, setSearchText] = useState("");
  const [inventoryList, setInventoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addInventoryUpdate, setAddInventoryUpdate] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [fetchingShop, setFetchingShop] = useState(false);

  const fetchInventoryLogs = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      const response = await InventoryUpdateServ.getLogs(params);
      const logsData = response?.data?.logs || [];
      setInventoryList(logsData);
      setTotalItems(response?.data?.total || 0);
    } catch (error) {
      console.error("Error fetching inventory logs:", error);
      message.error("Failed to fetch inventory logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (type) => {
    try {
      setLoading(true);
      const response = await ProductServ.getSellerProducts({ limit: 1000 });
      const allProducts = response?.data || response?.products || [];

      let filtered = [];
      if (type === "in-stock") {
        filtered = allProducts.filter(p => p.stock > 0);
      } else if (type === "out-of-stock") {
        filtered = allProducts.filter(p => p.stock === 0);
      }

      setProductList(filtered);
      setTotalItems(filtered.length);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    if (activeTab === "logs") {
      fetchInventoryLogs();
    } else {
      fetchProducts(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchInventoryLogs();
    }
  }, [page, pageSize]);

  const handlePageChange = (page) => {
    setPage(page);
    const pageContent = document.querySelector('.page-content');
    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const onShowSizeChange = (current, size) => setPageSize(size);

  const fetchShopDetails = async (shopId) => {
    if (!shopId) return;
    try {
      setFetchingShop(true);
      const res = await ShopServ.single(shopId);
      setSelectedShop(res?.data || res);
    } catch (error) {
      console.error("Error fetching shop details:", error);
    } finally {
      setFetchingShop(false);
    }
  };

  const handleRowClick = (record) => {
    const product = activeTab === "logs" ? record.productId : record;
    if (!product) return;

    setSelectedProduct(product);
    setSelectedShop(null);
    setDetailsModalVisible(true);

    const shopId = product.shopId?._id || product.shopId || product.userId?.shopId;
    if (shopId) fetchShopDetails(shopId);
  };

  const handleDeleteLog = async (id) => {
    try {
      await InventoryUpdateServ.remove(id);
      message.success("Record removed");
      fetchInventoryLogs();
    } catch (error) {
      console.error("Error deleting record:", error);
      message.error("Failed to remove record");
    }
  };

  const logColumns = [
    {
      title: "Image",
      key: "image",
      width: 70,
      align: "center",
      render: (_, record) => <TableImage src={getNormalizedImageUrl(record.productId?.imageUrl)} alt="product" />,
    },
    {
      title: "Product Name",
      dataIndex: ["productId", "name"],
      key: "productName",
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{name}</span>,
    },
    {
      title: "Quantity Updated",
      dataIndex: "quantityChange",
      key: "quantityChange",
      width: 120,
      align: "right",
      render: (val) => (
        <div className={`premium-tag ${val > 0 ? 'success' : 'error'}`}>
          {val > 0 ? `+${val}` : val}
        </div>
      )
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (user) => (
        <span style={{ fontWeight: 500 }}>
          {user ? `${user.firstName} ${user.lastName}` : "System"}
        </span>
      )
    },
    {
      title: "Updated At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "20%",
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: "15%",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 150,
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            type="primary"
            size="small"
            onClick={() => handleRowClick(record)}
            style={{
              borderRadius: 3,
              fontWeight: 600,
              background: '#ffffffff',
              color: '#004754ff',
              border: '1px solid #e4e4e7',
              boxShadow: '0 0 0 1px #e4e4e7',
              height: 'auto',
              padding: '0px 8px',
              fontSize: 13,
            }}
          >
            View
          </Button>
          <Popconfirm
            title="Are you sure to remove this log?"
            onConfirm={(e) => { e.stopPropagation(); handleDeleteLog(record._id); }}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} type="link" danger size="small" style={{ borderRadius: 6 }}> Remove </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const productColumns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "image",
      width: 70,
      align: "center",
      render: (url) => (
        <div className="image-hover-wrapper">
          <TableImage src={getNormalizedImageUrl(url)} alt="product" />
        </div>
      ),
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="product-name-hover">{name}</span>,
    },
    {
      title: "Current Stock",
      dataIndex: "stock",
      key: "stock",
      width: 120,
      align: "right",
      render: (stock) => (
        <div className={`premium-tag ${stock > 10 ? "success" : stock > 0 ? "warning" : "error"}`}>
          {stock} Units
        </div>
      )
    },
    //   title: "Price",
    //   dataIndex: "price",
    //   key: "price",
    //   width: 100,
    //   align: "right",
    //   render: (price) => <span style={{ fontWeight: 600, color: '#FF8800' }}>{formatPrice(price)}</span>
    // }
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(record);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      )
    }
  ];

  const filteredData = activeTab === "logs"
    ? inventoryList.filter(item => item.productId?.name?.toLowerCase().includes(searchText.toLowerCase()))
    : productList.filter(item => item.name?.toLowerCase().includes(searchText.toLowerCase()));

  const tabItems = [
    { key: "in-stock", label: <div className={`tab-label-premium success`}><span className="pcu-font-600">In Stock</span></div> },
    { key: "out-of-stock", label: <div className={`tab-label-premium error`}><span className="pcu-font-600">Out of Stock</span></div> },
  ];

  return (
    <div className="ss-wrapper">
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="ss-container">
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-header-icon">
              <InboxOutlined style={{ fontSize: 22, color: '#4776E6' }} />
            </div>
            <div>
              <h1 className="ss-title">Inventory Updates</h1>
              <p className="ss-subtitle">{totalItems} products listed</p>
            </div>
          </div>
          <Button
            className="ss-create-btn"
            onClick={() => setAddInventoryUpdate(true)}
          >
            <PlusOutlined /> Add Record
          </Button>
        </div>

        <div className="ss-filter-bar">
          <div className="ss-filter-field" style={{ flex: 0, minWidth: '300px' }}>
            <span className="ss-filter-label">Search</span>
            <div className="ss-search-wrap">
              <SearchOutlined className="ss-search-icon" />
              <input
                type="text"
                placeholder={`Search by ${activeTab === 'logs' ? 'Product Name' : 'Name'}...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="ss-search-input"
              />
            </div>
          </div>

          <div className="ss-filter-field" style={{ flex: 1 }}>
            <span className="ss-filter-label">Stock Status</span>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              className="ss-tabs-filter"
            />
          </div>

          <div className="ss-filter-reset">
            <button className="ss-reset-btn" onClick={() => setSearchText("")}>
              <ReloadOutlined /> Reset
            </button>
          </div>
        </div>

        <div className="ss-table-wrap">
          <Table
            className="custom-table table-container"
            columns={activeTab === "logs" ? logColumns : productColumns}
            dataSource={filteredData}
            rowKey="_id"
            locale={{
              emptyText: (
                <EmptyState
                  title={
                    activeTab === 'in-stock' ? "No In-Stock Products" :
                      "No Out-of-Stock Products"
                  }
                  description={searchText
                    ? "No records match your search."
                    : activeTab === 'logs' ? "Track your inventory changes here. Add a record to start logging."
                      : activeTab === 'in-stock' ? "You don't have any products with stock > 0."
                        : "Great news! You don't have any out-of-stock products."
                  }
                  gradient="green"
                  onAction={!searchText && activeTab === 'logs' ? () => setAddInventoryUpdate(true) : undefined}
                  actionText="Add Record"
                  icon="inbox"
                />
              )
            }}
            loading={loading}
            pagination={{
              className: "custom-pagination",
              current: page,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onShowSizeChange: onShowSizeChange,
              showTotal: (total) => `Total ${total} items`,
              onChange: handlePageChange,
              position: ['bottomRight'],
            }}
            bordered
          />
        </div>

        {addInventoryUpdate && (
          <AddInventoryUpdate
            open={addInventoryUpdate}
            onClose={() => setAddInventoryUpdate(false)}
            refreshInventory={() => {
              if (activeTab === "logs") fetchInventoryLogs();
              else fetchProducts(activeTab);
            }}
          />
        )}

        <Modal
          open={detailsModalVisible}
          onCancel={() => setDetailsModalVisible(false)}
          footer={null}
          width={650}
          closable={true}
          className="ss-modal"
        >
          {selectedProduct && (
            <div className="ss-modal-body">
              <div className="ss-modal-hero">
                <div className="ss-modal-img">
                  <TableImage src={getNormalizedImageUrl(selectedProduct.imageUrl)} alt={selectedProduct.name} size={110} radius={0} />
                </div>
                <div className="ss-modal-info">
                  <h2 className="ss-modal-name">{selectedProduct.name}</h2>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#FF8800', marginBottom: 10 }}>
                    {formatPrice(selectedProduct.price)}
                    {selectedProduct.mrp > selectedProduct.price && (
                      <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through', marginLeft: 8, fontWeight: 500 }}>
                        {formatPrice(selectedProduct.mrp)}
                      </span>
                    )}
                  </div>
                  <div className="ss-modal-tags">
                    <span className="ss-tag ss-tag-blue">{selectedProduct.categoryId?.name || "Category"}</span>
                    {selectedProduct.subCategoryId?.name && (
                      <span className="ss-tag ss-tag-orange">{selectedProduct.subCategoryId.name}</span>
                    )}
                    <span className={`ss-tag ${selectedProduct.stock > 0 ? 'ss-tag-green' : 'ss-tag-red'}`}>
                      Stock: {selectedProduct.stock} Units
                    </span>
                  </div>
                </div>
              </div>

              <div className="ss-modal-details">
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Brand</div>
                  <div className="ss-modal-value">{selectedProduct.brandId?.name || "N/A"}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Min. Order</div>
                  <div className="ss-modal-value">{selectedProduct.minOrderQuantity || 1}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Description</div>
                  <div className="ss-modal-value">{selectedProduct.description || "No description provided"}</div>
                </div>

                <div className="ss-modal-row" style={{ borderTop: '1px solid #f3f4f6', marginTop: 10 }}>
                  <div className="ss-modal-label">Associated Shop</div>
                  <div className="ss-modal-value">
                    {fetchingShop ? (
                      <span style={{ color: '#9ca3af' }}>Loading...</span>
                    ) : selectedShop ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <TableImage src={getNormalizedImageUrl(selectedShop.imageUrl)} alt={selectedShop.name} size={36} radius={8} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a1d2e', fontSize: 13 }}>{selectedShop.name}</div>
                          <div style={{ color: '#6b7280', fontSize: 11 }}>{selectedShop.location?.address}, {selectedShop.location?.city}</div>
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not available</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="ss-modal-footer">
                <button className="ss-modal-close-btn" onClick={() => setDetailsModalVisible(false)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default InventoryUpdate;

